import React from 'react';
import { Typography } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import SaveIcon from '@material-ui/icons/Save';
import CustomFilter from 'components/Shared/CustomFilter';
import { ExportCsv, ExportPdf } from 'utils/ExportUtils';
import { useGridsState } from 'hooks/useGridsState';
import { useAppContext } from 'hooks/useAppContext';
import { useAuthContext } from 'hooks/useAuthContext';
import { saveColumnVisibility, loadColumnVisibilityFromDb } from 'utils/ColumnVisibilityStorage';


const WrappedWithState = (props, rest) => {
  const { theme, getRecords, defaultFilters, exportFilter = false } = rest;
  const { gridStates, updateGridFilterIsActive } = useGridsState();
  const { systemUserId } = useAuthContext();
  let { selectedProject } = useAppContext();
  if (!selectedProject) {
    selectedProject = "@global"
  };
  const isFiltering = (title) => (selectedProject in gridStates && title in gridStates[selectedProject]) ? gridStates[selectedProject][title].filterActive : false;
  const checkFilter = (gridTitle, fieldName) => {
    let value = '';
    try {
      value = gridStates[selectedProject][gridTitle].filters[fieldName].filter;
      return value;
    } catch (e) {
      return value;
    }
  };

  const setFiltering = (gridTitle, yesno) => {
    updateGridFilterIsActive({ gridTitle, filterActive: yesno });
  };

  return React.memo(withAppStylesGrid(props, { theme, getRecords, defaultFilters, isFiltering, checkFilter, setFiltering, exportFilter, systemUserId, selectedProject }));
}


function withAppStylesGrid(WrappedComponent, { theme, getRecords, defaultFilters, isFiltering = () => false, checkFilter, setFiltering, exportFilter, systemUserId, selectedProject }) {

  return class extends React.Component {
    tableRef = React.createRef();
    // Track the most recent data request so a slow earlier response can't
    // overwrite a newer one (out-of-order results).
    latestRequestId = 0;
    constructor(props) {
      super(props);

      const isActiveColumnExists = !!this.props.columns.find(col => col.field.includes("IsActive"));
      const isActiveColumn = this.props.columns.find(col => col.field.includes("IsActive"));

      this.state = {
        filter: isFiltering(props.title),
        isActiveColumnExists,
        isActiveColumn,
        showInactive: false,
        updateDate: new Date().toString(),
        columnVisibility: {}
      };
      if (props.tableRef) {
        this.tableRef = props.tableRef;
      };
      this.getIsActiveColumnExists = this.getIsActiveColumnExists.bind(this);
      this.systemUserId = systemUserId;
      this.selectedProject = selectedProject;
    };

    setFilter = () => {
      this.setState({ filter: !this.state.filter });
      setFiltering(this.props.title, !this.state.filter);
    };

    exportRecords = async () => {
      if (this.tableRef.current) {
        const query = this.tableRef.current.state.query;
        const _filters = [...query.filters, { defaults: { ...defaultFilters } }];
        const args = [null, null, query.orderBy, query.orderDirection, query.search, _filters, exportFilter];
        const { data } = await getRecords(...args);
        return data;
      };
    }

    getIsActiveColumnExists = () => {
      return this.state.isActiveColumnExists;
    }

    saveColumnLayout = () => {
      // Save the current column visibility state to database
      if (this.tableRef.current && this.tableRef.current.state && this.tableRef.current.state.columns) {
        const currentColumns = this.tableRef.current.state.columns;
        const visibility = {};
        currentColumns.forEach(col => {
          if (col.field) {
            const isHidden = col.hidden === true;
            visibility[col.field] = isHidden;
          }
        });
        saveColumnVisibility(systemUserId, selectedProject, this.props.title, visibility);
      }
    }

    applyVisibilityToTable = (visibility) => {
      if (!this.tableRef.current || !this.tableRef.current.state || !this.tableRef.current.state.columns) return;

      const columns = this.tableRef.current.state.columns;
      if (columns.length === 0 || !columns[0].tableData) return;

      let hasChanges = false;
      columns.forEach(col => {
        if (col.field && visibility[col.field] !== undefined) {
          const shouldBeHidden = visibility[col.field];
          if (col.hidden !== shouldBeHidden) {
            col.hidden = shouldBeHidden;
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        this.tableRef.current.setState({ columns: [...columns] });
      }
    }

    componentDidMount() {
      // Load from database and apply
      if (systemUserId && selectedProject && selectedProject !== "@global") {
        loadColumnVisibilityFromDb(systemUserId, selectedProject, this.props.title)
          .then(dbVisibility => {
            if (dbVisibility && Object.keys(dbVisibility).length > 0) {
              this.setState({ columnVisibility: dbVisibility }, () => {
                // Wait for table to be ready then apply
                const applyWhenReady = (attempt = 0) => {
                  if (attempt > 10) return;
                  setTimeout(() => {
                    if (this.tableRef.current && this.tableRef.current.state &&
                        this.tableRef.current.state.columns &&
                        this.tableRef.current.state.columns.length > 0 &&
                        this.tableRef.current.state.columns[0].tableData) {
                      this.applyVisibilityToTable(dbVisibility);
                    } else {
                      applyWhenReady(attempt + 1);
                    }
                  }, 100 * (attempt + 1));
                };
                applyWhenReady(0);
              });
            }
          })
          .catch(err => console.error("Error loading column visibility from DB:", err));
      }
    }

    componentDidUpdate(prevProps) {
      // Check if systemUserId or selectedProject has changed
      if ((this.systemUserId !== systemUserId || this.selectedProject !== selectedProject) &&
          systemUserId && selectedProject) {
        this.systemUserId = systemUserId;
        this.selectedProject = selectedProject;

        // Reload column visibility with the correct auth values
        if (selectedProject !== "@global") {
          loadColumnVisibilityFromDb(systemUserId, selectedProject, this.props.title)
            .then(dbVisibility => {
              if (dbVisibility && Object.keys(dbVisibility).length > 0) {
                this.setState({ columnVisibility: dbVisibility }, () => {
                  this.applyVisibilityToTable(dbVisibility);
                });
              }
            })
            .catch(err => console.error("Error loading column visibility from DB:", err));
        }
      }
    }

    render() {
      // only have an isActive filter if show inactive is set to false, in which case we only query for active records... kinda tricky logic...
      const isActive = { ...this.state.isActiveColumnExists && !this.state.showInactive && { [this.state.isActiveColumn.field]: true } };
      const extendedProps = {
        style: { boxShadow: "0 0 0 0" },
        options: {
          ...(this.props.options && this.props.options) || {},
          search: false,
          headerStyle: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
          padding: "dense",
          thirdSortClick: false,
          columnsButton: true,
          exportButton: true,
          exportCsv: (columns, data) => ExportCsv(columns, data, this.props.title || 'data', this.exportRecords),
          exportPdf: (columns, data) => ExportPdf(columns, data, this.props.title || 'data', this.exportRecords)
        },
        actions: [
          ...(this.props.actions && this.props.actions) || [],
          {
            icon: () => <><FilterListIcon /><Typography variant="subtitle2">&nbsp;FILTER DATA</Typography></>,
            tooltip: 'Toggle Filters',
            isFreeAction: true,
            onClick: (event) => this.setFilter()
          },
          {
            icon: () => <><SaveIcon /><Typography variant="subtitle2">&nbsp;SAVE COLUMNS</Typography></>,
            tooltip: 'Save column layout',
            isFreeAction: true,
            onClick: (event) => this.saveColumnLayout()
          },
          ...(this.getIsActiveColumnExists() && [{
            icon: () => <>
              {this.state.showInactive ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              <Typography variant="subtitle2">&nbsp;SHOW INACTIVE</Typography>
            </>,
            tooltip: 'Show inactive records',
            isFreeAction: true,
            onClick: (event) => { this.setState({ showInactive: !this.state.showInactive, updateDate: new Date().toString() }); }
          }]) || []
        ],
        padding: "default",
        data: typeof (getRecords) === "function" ? query =>
          new Promise((resolve, reject) => {
            const page = query.page;
            const _filters = [...query.filters, { defaults: { ...defaultFilters, ...isActive } }];

            // Mark this as the latest request so a slow earlier response is ignored.
            const requestId = ++this.latestRequestId;

            getRecords(query.page, query.pageSize, query.orderBy, query.orderDirection, query.search, _filters)
              .then(r => {
                // Ignore a stale response: a newer request has superseded this one.
                if (requestId !== this.latestRequestId) return;
                resolve({
                  data: r.data,
                  page,
                  totalCount: r.count
                })
              })
              .catch(err => {
                if (requestId !== this.latestRequestId) return; // stale — ignore
                reject(err);
              })
          }) : getRecords
      };

      return (
        <>

          <WrappedComponent
            key={this.state.updateDate}
            tableRef={this.tableRef}
            {...{ ...this.props, ...extendedProps }}
            options={{ ...{ filtering: this.state.filter }, ...extendedProps.options }}
            columns={this.props.columns.map(r => {
              // Get saved visibility for this column
              const savedHidden = this.state.columnVisibility[r.field];

              // Use saved value if it exists, otherwise use the column's default
              const shouldBeHidden = savedHidden !== undefined
                ? savedHidden
                : (r.hiddenByColumnsButton || false);

              return {
                ...r,
                filterComponent: (props) => <CustomFilter {...props} title={this.props.title}></CustomFilter>,
                defaultFilter: checkFilter(this.props.title, r.field),
                hiddenByColumnsButton: shouldBeHidden
              };
            })}
          />
        </>
      )
    }
  }
};


export default WrappedWithState;
